import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Navigation } from "lucide-react";
import { useLanguage, CONTACT } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import BookingCalendar from "@/components/BookingCalendar";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contatti() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [formEnabled, setFormEnabled] = useState(null);
  const [bookingConfig, setBookingConfig] = useState(null);

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setFormEnabled(res.data.contact_form_enabled))
      .catch(() => setFormEnabled(true));
    api
      .get("/booking/config")
      .then((res) => setBookingConfig(res.data))
      .catch(() => {});
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contacts.form.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contacts.form.error);
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: Phone, label: t.contacts.info.phone, value: CONTACT.phone, testid: "contact-info-phone" },
    { icon: MapPin, label: t.contacts.info.address, value: `${CONTACT.address}, ${CONTACT.city}`, testid: "contact-info-address" },
    { icon: Clock, label: t.contacts.info.hours, value: lang === "it" ? CONTACT.hours_it : CONTACT.hours_en, testid: "contact-info-hours" },
  ];

  return (
    <div data-testid="contatti-page" className="pt-24 sm:pt-32">
      <section className="container-editorial pb-14">
        <Reveal>
          <p className="eyebrow">{t.contacts.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-slate-900 tracking-tight">
            {t.contacts.title}
          </h1>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-slate-600">{t.contacts.sub}</p>
        </Reveal>
      </section>

      <section className="container-editorial pb-20 sm:pb-28 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="md:col-span-5">
          <div className="space-y-7">
            {info.map((item) => (
              <div key={item.label} className="flex items-start gap-4" data-testid={item.testid}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
                  <item.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{item.label}</p>
                  <p className="mt-1 text-sm font-medium text-slate-800">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="md:col-span-7">
          {formEnabled === false ? (
            <div
              data-testid="contact-form-disabled"
              className="relative overflow-hidden border border-brand/30 bg-brand-linen p-8 sm:p-12 text-center"
            >
              <p className="eyebrow">{t.contacts.form.bookingEyebrow}</p>
              <p className="mx-auto mt-4 max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-snug text-slate-900">
                {t.contacts.form.disabledBody}
              </p>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                data-testid="contact-disabled-phone"
                className="mt-8 inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-base font-medium text-white transition-colors duration-300 hover:bg-brand-dark sm:inline-block sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:font-serif sm:text-2xl sm:font-light sm:text-brand sm:hover:bg-transparent sm:hover:text-brand-dark sm:lg:text-3xl"
              >
                <Phone className="h-5 w-5 shrink-0 sm:hidden" />
                <span className="border-b-2 border-transparent transition-colors duration-300 group-hover:border-white sm:border-b sm:border-brand/30 sm:pb-1 sm:group-hover:border-brand">
                  {CONTACT.phone}
                </span>
              </a>
            </div>
          ) : (
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-slate-200 bg-brand-linen p-7 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500" htmlFor="contact-name">
                  {t.contacts.form.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={update("name")}
                  className="mt-2 w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500" htmlFor="contact-email">
                  {t.contacts.form.email}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
                />
              </div>
            </div>
            <div className="mt-5">
              <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500" htmlFor="contact-phone">
                {t.contacts.form.phone}
              </label>
              <input
                id="contact-phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                className="mt-2 w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand"
              />
            </div>
            <div className="mt-5">
              <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500" htmlFor="contact-message">
                {t.contacts.form.message}
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-input-message"
                required
                minLength={10}
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="mt-2 w-full border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand resize-none"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="mt-7 rounded-full bg-brand px-8 py-3.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? t.contacts.form.sending : t.contacts.form.submit}
            </button>
          </form>
          )}
        </Reveal>
      </section>

      <section className="container-editorial pb-20 sm:pb-28">
        {bookingConfig?.enabled && (
          <Reveal>
            <div className="mb-16" data-testid="booking-section">
              <BookingCalendar config={bookingConfig} />
            </div>
          </Reveal>
        )}
        <Reveal>
          <div className="border border-slate-200 h-80 overflow-hidden relative" data-testid="contact-map">
            <iframe
              title={`Mappa studio medico — ${CONTACT.fullAddress}`}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${(CONTACT.lon - 0.0055).toFixed(5)}%2C${(CONTACT.lat - 0.0035).toFixed(5)}%2C${(CONTACT.lon + 0.0055).toFixed(5)}%2C${(CONTACT.lat + 0.0035).toFixed(5)}&layer=mapnik&marker=${CONTACT.lat}%2C${CONTACT.lon}`}
              className="map-grayscale w-full h-full"
            />
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CONTACT.fullAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-directions-button"
              className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-brand-dark transition-colors duration-300"
            >
              <Navigation className="h-4 w-4" /> {t.contacts.directions}
            </a>
          </div>
          <p className="mt-3">
            <a
              href={`https://www.openstreetmap.org/directions?to=${CONTACT.lat}%2C${CONTACT.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-openstreetmap-link"
              className="text-xs font-light text-slate-400 link-underline"
            >
              {t.contacts.openMap}
            </a>
          </p>
        </Reveal>
      </section>
    </div>
  );
}
